import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  LinearProgress,
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Speed as SpeedIcon,
  CloudUpload as CloudUploadIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import ExcelFileUpload from '../components/excel/ExcelFileUpload';
import ColumnMapping from '../components/excel/ColumnMappingNew';
import CompanySelector from '../components/excel/CompanySelector';
import { SchemaSaveDialog } from '../components/excel/SchemaSaveDialog';
import { CalculationProgress } from '../components/excel/CalculationProgress';
import { LegCountSelector } from '../components/excel/LegCountSelector';
import { InlineColumnMapper } from '../components/excel/InlineColumnMapper';
import SchemaLoader from '../components/excel/SchemaLoader';
import PageHeader from '../components/common/PageHeader';
import { ManualEntry } from '../components/excel/ManualFieldEntry';
import DelimiterSelector from '../components/excel/DelimiterSelector';
import { excelApi } from '../services/api/excelApi';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { CsvDelimiter } from '../utils/csvDelimiter';
import { parseSpreadsheetFile } from '../utils/spreadsheetParser';

export interface ExcelData {
  headers: string[];
  rows: string[][];
  totalRows: number;
  delimiter?: CsvDelimiter;
}

export interface ColumnMappingData {
  [key: string]: string;
}

export interface SchemaData {
  name: string;
  mappings: ColumnMappingData;
  legCount?: number;
  mappingType?: 'inline' | 'manual';
  settings: {
    hasHeader: boolean;
    useStreetDistances: boolean;
    standard: 'ISO' | 'EN';
  };
  id?: string;
  // Owning licensee/company — populated by the backend; used to scope the saved-mapping list.
  companyId?: number;
  // DB metadata — populated by the backend when available
  createdAt?: string;
  updatedAt?: string;
  lastModifiedDate?: string;
  createdByName?: string;
  updatedByName?: string;
  createdByType?: string;
  updatedByType?: string;
}

// ─── Step definitions ────────────────────────────────────────────────────────

interface StepDef {
  label: string;
  description: string;
}

const INLINE_STEPS: StepDef[] = [
  { label: 'Upload', description: 'Licensee & file' },
  { label: 'Configure Legs', description: 'Set leg count & schema' },
  { label: 'Map Columns', description: 'Assign field mappings' },
];

const TRADITIONAL_STEPS: StepDef[] = [
  { label: 'Upload', description: 'Licensee & file' },
  { label: 'Load Schema', description: 'Optional template' },
  { label: 'Map Columns', description: 'Drag & drop mapping' },
];

// Step indices (same for both method variants)
const STEP_UPLOAD = 0;
const STEP_CONFIGURE = 1; // leg count or schema selection
const STEP_MAP = 2;

// ─── Pure helpers (module scope: no component state, keeps component complexity low) ──

const mappingsEqual = (a: ColumnMappingData, b: ColumnMappingData): boolean => {
  const toSorted = (m: ColumnMappingData) =>
    JSON.stringify(Object.entries(m).filter(([, v]) => v).sort(([a], [b]) => a.localeCompare(b)));
  return toSorted(a) === toSorted(b);
};

const cleanMappings = (mappings: ColumnMappingData): ColumnMappingData => {
  const cleaned: ColumnMappingData = {};
  Object.entries(mappings).forEach(([key, value]) => {
    // Accept numeric index keys and MANUAL: keys; deduplicate field IDs
    if (key.startsWith('MANUAL:') || !Number.isNaN(Number.parseInt(key, 10))) {
      if (!Object.values(cleaned).includes(value)) {
        cleaned[key] = value;
      }
    }
  });
  return cleaned;
};

const inferLegCountFromMappings = (mappings: ColumnMappingData): number => {
  const legNums = Object.values(mappings)
    .map(f => {
      const m = /_leg(\d+)$/.exec(f);
      return m ? Number.parseInt(m[1], 10) : 0;
    })
    .filter(n => n > 0);
  return legNums.length > 0 ? Math.max(...legNums) : 1;
};

// Sort key for the saved-mapping list: the same timestamp shown on each row (updated → modified → created).
const schemaSortTime = (s: SchemaData): number => {
  const raw = s.updatedAt || s.lastModifiedDate || s.createdAt;
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

// Build the final mappings payload by merging manual entries onto the column mappings.
const buildMergedMappings = (
  columnMappings: ColumnMappingData,
  manualEntries: ManualEntry[],
): ColumnMappingData => {
  const merged = { ...columnMappings };
  manualEntries.forEach(entry => {
    if (entry.fieldId && entry.value) {
      merged[`MANUAL:${entry.value}`] = entry.fieldId;
    }
  });
  return merged;
};

// ─── Licensee-only upload view ────────────────────────────────────────────────
// Licensee Admins can only perform raw uploads. No schema mapping, no calculation.
// CarbonCareAdmin / MasterAdmin continue the flow later from the Uploaded Files page.

const LicenseeUploadView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const laHandleFileUpload = async (file: File) => {
    if (!user?.companyId) {
      setError('Your account is not linked to a licensee. Please contact support.');
      return;
    }
    setUploadedFile(file);
    setProcessing(true);
    setError(null);
    setUploadProgress(0);
    try {
      await excelApi.uploadFile(file, {
        status: 'uploaded',
        companyId: user.companyId,
        onProgress: (pct) => setUploadProgress(pct),
      });
      navigate('/files/uploaded', {
        state: { uploadedToast: 'File uploaded successfully' },
      });
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to upload file');
      setUploadProgress(0);
      setUploadedFile(null);
      setProcessing(false);
    }
  };

  return (
    <Box>
      <PageHeader description="Upload an Excel file. A CarbonCare admin will configure the schema mapping and run the calculation." />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          {processing && uploadedFile ? (
            <Box
              sx={{
                minHeight: 220,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                borderRadius: 3,
                border: '2px dashed',
                borderColor: 'primary.light',
                bgcolor: 'grey.50',
                p: 4,
              }}
            >
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {uploadedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Uploading… {uploadProgress}%
              </Typography>
              <Box sx={{ width: '100%', maxWidth: 360 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            </Box>
          ) : (
            <ExcelFileUpload
              onFileUpload={laHandleFileUpload}
              maxFileSize={10}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ExcelUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [activeStep, setActiveStep] = useState(STEP_UPLOAD);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [excelData, setExcelData] = useState<ExcelData | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMappingData>({});
  const [manualEntries, setManualEntries] = useState<ManualEntry[]>([]);
  const [savedSchemas, setSavedSchemas] = useState<SchemaData[]>([]);
  const [selectedSchema, setSelectedSchema] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(null);

  const [processing, setProcessing] = useState(false);
  const [activeProcessMode, setActiveProcessMode] = useState<'immediate' | 'background' | 'upload-only' | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [calculationId] = useState<string | null>(null);
  const [processedFileId] = useState<string | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [mappingMethod] = useState<'inline' | 'traditional'>('inline');
  const [legCount, setLegCount] = useState<number>(1);
  const [isMappingValid, setIsMappingValid] = useState(false);
  const [hasHeader, setHasHeader] = useState(true);
  const [skipErrorRows, setSkipErrorRows] = useState(false);
  const [errorRowIndices, setErrorRowIndices] = useState<number[]>([]);

  // Schema save dialog — shown before processing when mappings are new or changed
  const [schemaSaveDialogOpen, setSchemaSaveDialogOpen] = useState(false);
  const [pendingProcessMode, setPendingProcessMode] = useState<
    'immediate' | 'background' | 'upload-only' | null
  >(null);

  const isMasterAdmin =
    user?.accountType === AccountType.MasterAdmin ||
    user?.accountType === AccountType.CarbonCareAdmin;

  const steps = mappingMethod === 'inline' ? INLINE_STEPS : TRADITIONAL_STEPS;

  // ─── Init ──────────────────────────────────────────────────────────────────

  // Auto-set company ID for non-admin users
  useEffect(() => {
    if (user && !isMasterAdmin && user.companyId) {
      setSelectedCompanyId(user.companyId);
    }
  }, [user, isMasterAdmin]);

  // Fetch company name when ID is set
  useEffect(() => {
    if (!selectedCompanyId) {
      setSelectedCompanyName(null);
      return;
    }
    (async () => {
      try {
        const { companyApi } = await import('../services/api/companyApi');
        const company = await companyApi.getById(selectedCompanyId);
        setSelectedCompanyName(company.name);
      } catch {
        setSelectedCompanyName(null);
      }
    })();
  }, [selectedCompanyId]);

  // Load schemas once
  useEffect(() => {
    (async () => {
      try {
        const schemas = await excelApi.getSchemas();
        setSavedSchemas(schemas);
      } catch (e) {
        console.error('Failed to load schemas:', e);
      }
    })();
  }, []);

  // The schema currently loaded (if any)
  const loadedSchema = useMemo(
    () => savedSchemas.find(s => s.name === selectedSchema),
    [savedSchemas, selectedSchema]
  );

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleFileUpload = useCallback((file: File, data: ExcelData) => {
    setUploadedFile(file);
    setExcelData(data);
    setError(null);
    setActiveStep(STEP_CONFIGURE);
  }, []);

  // Re-reads the file with a delimiter the user chose by hand. Column indices move,
  // so any mapping made against the old parse has to go with it.
  const handleDelimiterChange = useCallback(
    async (delimiter: CsvDelimiter) => {
      if (!uploadedFile) return;
      try {
        const reparsed = await parseSpreadsheetFile(uploadedFile, delimiter);
        setExcelData(reparsed);
        setColumnMappings({});
        setManualEntries([]);
        setSkipErrorRows(false);
        setErrorRowIndices([]);
        setIsMappingValid(false);
        setError(null);
      } catch (e: any) {
        setError(e?.message || 'Failed to re-read the file with that delimiter');
      }
    },
    [uploadedFile]
  );

  const handleColumnMappingChange = useCallback(
    (mappings: ColumnMappingData, entries: ManualEntry[] = []) => {
      setColumnMappings(mappings);
      setManualEntries(entries);
      // Error rows were computed against the previous mappings
      setSkipErrorRows(false);
      setErrorRowIndices([]);
    },
    []
  );

  const handleSkipErrorRows = useCallback((skip: boolean, indices: number[]) => {
    setSkipErrorRows(skip);
    setErrorRowIndices(indices);
  }, []);

  // Inline: loading a schema infers leg count; Next advances to Map Columns
  // Inline schemas use column index keys (same format as cleanMappings), safe to apply directly.
  const handleInlineSchemaLoad = useCallback((schema: SchemaData) => {
    setColumnMappings(schema.mappings);
    setSelectedSchema(schema.name);
    setLegCount(schema.legCount ?? inferLegCountFromMappings(schema.mappings));
    setHasHeader(schema.settings?.hasHeader ?? true);
  }, []);

  const handleSchemaSave = useCallback(
    async (schemaName: string, settings: SchemaData['settings'], existingSchemaId?: string) => {
      const mappingType =
        mappingMethod === 'traditional' ? 'manual' : mappingMethod;
      const schemaData: SchemaData = {
        name: schemaName,
        mappings: columnMappings,
        legCount,
        mappingType,
        settings,
      };
      if (existingSchemaId) {
        await excelApi.updateSchema(existingSchemaId, schemaData);
      } else {
        await excelApi.createSchema(schemaData);
      }
      const updated = await excelApi.getSchemas();
      setSavedSchemas(updated);
      setSelectedSchema(schemaName);
      setSuccess(`Schema "${schemaName}" ${existingSchemaId ? 'updated' : 'saved'} successfully.`);
    },
    [columnMappings, legCount, mappingMethod]
  );

  const handleSchemaDelete = useCallback(async (schema: SchemaData) => {
    if (!schema.id) throw new Error('Schema has no ID — cannot delete.');
    await excelApi.deleteSchema(schema.id);
    setSavedSchemas(prev => prev.filter(s => s.id !== schema.id));
  }, []);

  const handleSchemaClear = useCallback(() => {
    setSelectedSchema('');
    setColumnMappings({});
  }, []);

  const handleNext = () => {
    if (activeStep === STEP_UPLOAD) {
      if (isMasterAdmin && !selectedCompanyId) {
        setError('Please select a company before proceeding.');
        return;
      }
      if (!uploadedFile) {
        setError('Please upload a file before proceeding.');
        return;
      }
    }

    if (
      activeStep === STEP_MAP &&
      mappingMethod === 'inline' &&
      !isMappingValid
    ) {
      setError('Please map all required fields before proceeding.');
      return;
    }

    if (
      activeStep === STEP_MAP &&
      mappingMethod === 'traditional' &&
      Object.keys(columnMappings).length === 0
    ) {
      setError('Please map at least one column before proceeding.');
      return;
    }

    setError(null);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep(prev => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(STEP_UPLOAD);
    setUploadedFile(null);
    setExcelData(null);
    setColumnMappings({});
    setSelectedSchema('');
    setError(null);
    setSuccess(null);
  };

  const executeProcess = async (
    processMode: 'immediate' | 'background' | 'upload-only' = 'immediate',
    overrideSettings?: SchemaData['settings'],
  ) => {
    if (!uploadedFile || !excelData) {
      setError('No file to process.');
      return;
    }
    if (!selectedCompanyId) {
      setError('Please select a company before processing.');
      return;
    }

    setProcessing(true);
    setActiveProcessMode(processMode);
    setUploadProgress(0);
    setError(null);
    setAnchorEl(null);

    const onProgress = (percent: number) => setUploadProgress(percent);

    try {
      const settings: SchemaData['settings'] = overrideSettings || {
        ...(loadedSchema?.settings || { useStreetDistances: true, standard: 'ISO' as const }),
        hasHeader,
      };

      const mergedMappings = buildMergedMappings(columnMappings, manualEntries);

      if (processMode === 'upload-only') {
        const selectedSchemaObj = savedSchemas.find(
          s => s.name === selectedSchema
        );
        await excelApi.uploadFile(uploadedFile, {
          status: 'uploaded',
          mappings: mergedMappings,
          schemaId: selectedSchemaObj?.id,
          companyId: selectedCompanyId || undefined,
          onProgress,
          schemaName: selectedSchema || undefined,
          settings,
          delimiter: excelData?.delimiter,
        });
        navigate('/files/uploaded');
        return;
      } else {
        await excelApi.processFile(
          uploadedFile,
          mergedMappings,
          settings,
          selectedCompanyId,
          selectedSchema,
          skipErrorRows ? errorRowIndices : undefined,
          onProgress,
          excelData?.delimiter
        );

        navigate('/files/processing');
        return;
      }
    } catch (e: any) {
      setError(e.message || 'Failed to process file. Please try again.');
      setProcessing(false);
      setUploadProgress(0);
    }
  };

  // Intercept process button: check if schema save dialog is needed first
  const handleProcess = (
    processMode: 'immediate' | 'background' | 'upload-only' = 'immediate'
  ) => {
    setAnchorEl(null);
    const settingsChanged = loadedSchema && hasHeader !== (loadedSchema.settings?.hasHeader ?? true);
    const needsDialog =
      !loadedSchema || !mappingsEqual(columnMappings, loadedSchema.mappings) || settingsChanged;
    if (needsDialog) {
      setPendingProcessMode(processMode);
      setSchemaSaveDialogOpen(true);
    } else {
      executeProcess(processMode);
    }
  };

  const handleSchemaDialogSaveAndProcess = async (
    schemaName: string,
    dialogSettings: SchemaData['settings'],
    existingSchemaId?: string
  ) => {
    setSchemaSaveDialogOpen(false);
    try {
      await handleSchemaSave(schemaName, dialogSettings, existingSchemaId);
    } catch (e: any) {
      setError(e.message || 'Failed to save schema.');
      setPendingProcessMode(null);
      return;
    }
    if (pendingProcessMode) executeProcess(pendingProcessMode, dialogSettings);
    setPendingProcessMode(null);
  };

  const handleSchemaDialogSkip = (dialogSettings: SchemaData['settings']) => {
    setSchemaSaveDialogOpen(false);
    if (pendingProcessMode) executeProcess(pendingProcessMode, dialogSettings);
    setPendingProcessMode(null);
  };

  // ─── Step content ────────────────────────────────────────────────────────────

  const filteredSchemas = useMemo(() => {
    const target = mappingMethod === 'traditional' ? 'manual' : mappingMethod;
    return savedSchemas
      .filter(s => s.mappingType === target || (!s.mappingType && target === 'manual'))
      // Scope to the account being worked on. Licensee users are already scoped
      // server-side; this narrows the admin view to the selected company so the list
      // isn't a full screen of every account's mappings. No company selected ⇒ unfiltered.
      .filter(s => selectedCompanyId == null || s.companyId === selectedCompanyId)
      // Latest-modified first, matching the date shown on each row.
      .sort((a, b) => schemaSortTime(b) - schemaSortTime(a));
  }, [savedSchemas, mappingMethod, selectedCompanyId]);

  const renderUploadStep = () => (
    <Box>
      {/* Company selection — only for Master/CarbonCare Admin */}
      {isMasterAdmin && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            bgcolor: 'grey.50',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Select Company
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Choose the company this file belongs to. You can then upload the file below.
          </Typography>
          <CompanySelector
            selectedCompanyId={selectedCompanyId}
            onCompanySelect={setSelectedCompanyId}
            showHeader={false}
          />
        </Paper>
      )}

      {/* File upload zone — locked for admin until company is chosen */}
      <Box
        sx={{
          opacity: isMasterAdmin && !selectedCompanyId ? 0.45 : 1,
          pointerEvents:
            isMasterAdmin && !selectedCompanyId ? 'none' : 'auto',
          transition: 'opacity 0.25s ease',
        }}
      >
        <ExcelFileUpload onFileUpload={handleFileUpload} maxFileSize={10} />
      </Box>
    </Box>
  );

  const renderConfigureStep = () => {
    if (mappingMethod === 'inline') {
      return excelData ? (
        <LegCountSelector
          legCount={legCount}
          onLegCountChange={setLegCount}
          excelData={excelData}
          savedSchemas={savedSchemas.filter(s => s.mappingType === 'inline')}
          loadedSchemaName={selectedSchema}
          onSchemaLoad={handleInlineSchemaLoad}
          onSchemaClear={handleSchemaClear}
          onSchemaDelete={handleSchemaDelete}
        />
      ) : null;
    }
    // Traditional: dedicated schema loader step
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Load a Schema
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Optionally load a saved schema to pre-fill your column mappings, then
          proceed to mapping. You can also skip this step and map manually.
        </Typography>
        <SchemaLoader
          schemas={filteredSchemas}
          loadedSchemaName={selectedSchema}
          onLoad={schema => {
            setColumnMappings(cleanMappings(schema.mappings));
            setSelectedSchema(schema.name);
          }}
          onDelete={handleSchemaDelete}
          title="Saved Schemas"
          subtitle="Select a schema from your saved templates to pre-fill mappings."
        />
      </Box>
    );
  };

  const renderMapStep = () => {
    if (mappingMethod === 'inline') {
      return excelData ? (
        <InlineColumnMapper
          excelData={excelData}
          legCount={legCount}
          existingMappings={columnMappings}
          existingManualEntries={manualEntries}
          onMappingChange={handleColumnMappingChange}
          onValidationChange={setIsMappingValid}
          onSwitchMethod={() => {}}
          onChangeLegCount={() => setActiveStep(STEP_CONFIGURE)}
          hasHeader={hasHeader}
          onHasHeaderChange={setHasHeader}
          onSkipErrorRows={handleSkipErrorRows}
        />
      ) : null;
    }
    return excelData ? (
      <ColumnMapping
        excelData={excelData}
        mappings={columnMappings}
        onMappingChange={handleColumnMappingChange}
      />
    ) : null;
  };

  const renderStepContent = (step: number) => {
    // Only CSV parses carry a delimiter; a wrong one scrambles every column, so the
    // control sits above the data on both steps where the columns are visible.
    const delimiterBar = excelData?.delimiter && step !== STEP_UPLOAD ? (
      <Box sx={{ mb: 2 }}>
        <DelimiterSelector
          delimiter={excelData.delimiter}
          onChange={handleDelimiterChange}
        />
      </Box>
    ) : null;

    switch (step) {
      case STEP_UPLOAD:
        return renderUploadStep();
      case STEP_CONFIGURE:
        return <>{delimiterBar}{renderConfigureStep()}</>;
      case STEP_MAP:
        return <>{delimiterBar}{renderMapStep()}</>;
      default:
        return null;
    }
  };

  // ─── Navigation bar helpers ──────────────────────────────────────────────────

  const isLastStep = activeStep === steps.length - 1;
  const isFirstStep = activeStep === 0;

  const nextDisabled =
    (activeStep === STEP_UPLOAD && (!uploadedFile || (isMasterAdmin && !selectedCompanyId))) ||
    (activeStep === STEP_MAP &&
      mappingMethod === 'inline' &&
      !isMappingValid) ||
    (activeStep === STEP_MAP &&
      mappingMethod === 'traditional' &&
      Object.keys(columnMappings).length === 0);

  // ─── Render ─────────────────────────────────────────────────────────────────

  // Licensee Admins can only perform raw uploads — render the simplified view.
  if (user?.accountType === AccountType.LicenseeAdmin) {
    return <LicenseeUploadView />;
  }

  return (
    <Box>
      <PageHeader
        description="Upload Excel files for emissions calculation."
        actions={
          selectedCompanyName ? (
            <Chip
              icon={<BusinessIcon sx={{ fontSize: '16px !important' }} />}
              label={selectedCompanyName}
              color="primary"
              sx={{ fontWeight: 700, fontSize: '0.8rem', px: 0.5, height: 32, flexShrink: 0 }}
            />
          ) : undefined
        }
      />

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stepper */}
      <Box
        sx={{
          mb: 3,
          px: 3,
          py: 1.5,
          bgcolor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {steps.map((step, index) => {
          const isCompleted = index < activeStep;
          const isActive = index === activeStep;
          return (
            <React.Fragment key={step.label}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {/* Circle */}
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: (() => {
                      if (isCompleted) return 'success.main';
                      if (isActive) return 'primary.main';
                      return 'grey.300';
                    })(),
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {isCompleted ? (
                    <CheckIcon sx={{ fontSize: 13, color: 'white' }} />
                  ) : (
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isActive ? 'white' : 'grey.600', lineHeight: 1 }}>
                      {index + 1}
                    </Typography>
                  )}
                </Box>
                {/* Label + description */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isActive ? 700 : 500,
                      color: (() => {
                        if (isActive) return 'text.primary';
                        if (isCompleted) return 'text.secondary';
                        return 'text.disabled';
                      })(),
                      lineHeight: 1.2,
                      fontSize: '0.8rem',
                    }}
                  >
                    {step.label}
                  </Typography>
                  {isActive && (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, display: 'block' }}>
                      {step.description}
                    </Typography>
                  )}
                </Box>
              </Box>
              {/* Connector */}
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: '1px',
                    mx: 1.5,
                    bgcolor: index < activeStep ? 'success.main' : 'grey.300',
                    transition: 'background-color 0.2s ease',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Main content card */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          {renderStepContent(activeStep)}

          {activeStep < steps.length && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                {/* Back */}
                <Button
                  disabled={isFirstStep}
                  onClick={handleBack}
                  variant="outlined"
                  color="inherit"
                  sx={{
                    color: 'text.secondary',
                    borderColor: 'grey.300',
                    '&:disabled': { opacity: 0.35 },
                  }}
                >
                  Back
                </Button>

                {/* Next / Process */}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  {isLastStep ? (
                    <>
                      <Tooltip
                        title={isMappingValid ? '' : 'Complete all required field mappings before processing'}
                        arrow
                        placement="top"
                      >
                        <span>
                          <Button
                            variant="contained"
                            onClick={e => {
                              if (!isMappingValid) {
                                setError('Please map all required fields before processing. Check the Mapping Status panel above for missing fields.');
                                return;
                              }
                              setAnchorEl(e.currentTarget);
                            }}
                            disabled={processing || !isMappingValid}
                            endIcon={<ArrowDropDownIcon />}
                            startIcon={<PlayArrowIcon />}
                            size="large"
                          >
                            Process File
                          </Button>
                        </span>
                      </Tooltip>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 300 } } }}
                      >
                        <MenuItem
                          onClick={() => handleProcess('immediate')}
                          sx={{ py: 1.25 }}
                        >
                          <ListItemIcon>
                            <SpeedIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Process & Calculate Now"
                            secondary="Stay on this page and watch real-time progress"
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleProcess('background')}
                          sx={{ py: 1.25 }}
                        >
                          <ListItemIcon>
                            <ScheduleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Process in Background"
                            secondary="Navigate away — check results in Calculated Files"
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                        </MenuItem>
                        <Divider />
                        <MenuItem
                          onClick={() => handleProcess('upload-only')}
                          sx={{ py: 1.25 }}
                        >
                          <ListItemIcon>
                            <CloudUploadIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Upload Only"
                            secondary="Save the file now and calculate later"
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                        </MenuItem>
                      </Menu>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={nextDisabled}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
          )}

          {activeStep === steps.length && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" gutterBottom>
                All done!
              </Typography>
              <Button onClick={handleReset} variant="outlined">
                Upload Another File
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Calculation progress dialog */}
      <CalculationProgress
        open={showProgress}
        onClose={() => setShowProgress(false)}
        calculationId={calculationId || undefined}
        fileId={processedFileId || undefined}
      />

      {/* Schema save dialog — shown before processing */}
      <SchemaSaveDialog
        open={schemaSaveDialogOpen}
        onClose={() => { setSchemaSaveDialogOpen(false); setPendingProcessMode(null); }}
        onSaveAndProcess={handleSchemaDialogSaveAndProcess}
        onProcessWithoutSaving={handleSchemaDialogSkip}
        schemas={filteredSchemas}
        loadedSchema={loadedSchema}
        currentMappings={columnMappings}
        headers={excelData?.headers}
        companyCode={selectedCompanyId || undefined}
        currentHasHeader={hasHeader}
      />

      {/* Upload Progress Dialog */}
      <Dialog
        open={processing}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 340, textAlign: 'center', py: 2 } }}
      >
        <DialogContent>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {uploadProgress < 100 ? 'Uploading file…' : 'Processing…'}
          </Typography>
          {uploadProgress < 100 ? (
            <>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ borderRadius: 1, height: 6, mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {uploadProgress}% uploaded
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {activeProcessMode === 'upload-only'
                ? 'Saving file…'
                : 'Saving file and preparing calculation…'}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Please do not close or navigate away.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ExcelUploadPage;
